import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, ShieldCheck, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AlumniReviews({ alumniId }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [count, setCount] = useState(0);
  const [average, setAverage] = useState(0);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/alumni/${alumniId}/reviews`).then((r) => {
      setReviews(r.data.reviews || []);
      setCount(r.data.count || 0);
      setAverage(r.data.average || 0);
    });
  };
  useEffect(load, [alumniId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in to leave a review."); return; }
    if (body.length < 10) { toast.error("Write a longer review."); return; }
    setBusy(true);
    try {
      await api.post(`/alumni/${alumniId}/reviews`, { rating, title, body });
      toast.success("Thank you — your review is live.");
      setTitle(""); setBody(""); setRating(5);
      load();
    } catch (e) {
      if (e.response?.status === 400) toast.error(e.response.data?.detail || "Could not post review.");
      else toast.error("Could not post review.");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-16" data-testid="alumni-reviews">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="overline">Reviews</div>
          <div className="mt-2 flex items-baseline gap-4">
            <div className="font-serif-display text-4xl">{count > 0 ? average.toFixed(1) : "—"}</div>
            <div className="flex items-center gap-1 text-[color:var(--wk-gold)]">
              {[1,2,3,4,5].map((s) => (
                <Star key={s} size={16} fill={s <= Math.round(average) ? "currentColor" : "transparent"} className={s <= Math.round(average) ? "" : "opacity-40"} />
              ))}
            </div>
            <div className="text-sm text-[color:var(--wk-bone-dim)]">{count} review{count === 1 ? "" : "s"}</div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {reviews.length === 0 && (
          <div className="col-span-full card-lux p-8 text-center text-[color:var(--wk-bone-dim)]">
            <MessageCircle className="text-[color:var(--wk-gold)] mx-auto" />
            <div className="mt-3 font-serif-display text-2xl text-[color:var(--wk-bone)]">Be the first to leave a review.</div>
            <div className="mt-1 text-sm">Verified reviews come from Wolfkeeper students, alumni, and families.</div>
          </div>
        )}
        {reviews.map((r) => (
          <motion.article key={r.review_id} initial={{ opacity: 0, y: 6 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="card-lux p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 text-[color:var(--wk-gold)]">
                {[1,2,3,4,5].map((s) => <Star key={s} size={12} fill={s <= r.rating ? "currentColor" : "transparent"} className={s <= r.rating ? "" : "opacity-40"} />)}
              </div>
              {r.verified && (
                <span className="flex items-center gap-1 text-[10px] overline text-[color:var(--wk-gold)]" data-testid="review-verified">
                  <ShieldCheck size={12} /> Verified
                </span>
              )}
            </div>
            <div className="mt-3 font-serif-display text-xl leading-tight">"{r.title}"</div>
            <p className="mt-2 text-[color:var(--wk-bone-dim)] leading-relaxed">{r.body}</p>
            <div className="mt-4 pt-3 border-t border-white/10 text-xs text-[color:var(--wk-bone-dim)]">
              — {r.reviewer_name}{r.program_context ? ` · ${r.program_context}` : ""} · {new Date(r.updated_at || r.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })}
            </div>
          </motion.article>
        ))}
      </div>

      {/* Form */}
      <div className="mt-10 card-lux p-8">
        <div className="font-serif-display text-2xl">Leave a review</div>
        {!user ? (
          <div className="mt-3 text-[color:var(--wk-bone-dim)] text-sm">
            <Link to="/login" className="text-[color:var(--wk-gold)] underline">Sign in</Link> to leave a review. Wolfkeeper alumni and students are auto-verified.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 grid gap-5" data-testid="alumni-review-form">
            <div>
              <div className="overline">Your rating</div>
              <div className="mt-2 flex items-center gap-1">
                {[1,2,3,4,5].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setRating(s)}
                    data-testid={`review-star-${s}`}
                    className="p-1"
                    aria-label={`${s} star`}
                  >
                    <Star size={22} className={s <= rating ? "text-[color:var(--wk-gold)]" : "text-white/25"} fill={s <= rating ? "currentColor" : "transparent"} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="overline">Headline</label>
              <input required value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140} placeholder="A short line that captures your experience" className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--wk-gold)] outline-none py-2 text-lg" data-testid="review-title" />
            </div>
            <div>
              <label className="overline">Your review</label>
              <textarea required rows={4} value={body} onChange={(e) => setBody(e.target.value)} maxLength={2400} placeholder="What did you learn? How did they help your dog?" className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--wk-gold)] outline-none py-2 text-base resize-none" data-testid="review-body" />
              <div className="mt-1 text-xs text-[color:var(--wk-bone-dim)] text-right">{body.length}/2400</div>
            </div>
            <button disabled={busy} className="btn-gold w-fit" data-testid="review-submit">
              {busy ? "Publishing…" : "Publish review"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
